import { useEffect, useState } from 'react';
import axios from 'axios';
import BlogCard from '@/components/BlogCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const fetchData = async () => {
      try {
        const [blogsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blogs?limit=50`),
          axios.get(`${API}/categories`)
        ]);
        
        setBlogs(blogsRes.data);
        setFilteredBlogs(blogsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (categoryFilter === 'all') {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(blogs.filter(blog => blog.category === categoryFilter));
    }
  }, [categoryFilter, blogs]);

  return (
    <div className="pt-[100px]">
      {/* Hero Header */}
      <div className="bg-primary py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="label-tag text-[hsl(var(--gold))] mb-3 block" data-testid="deals-eyebrow">Deals & Blogs</span>
            <h1 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-4" data-testid="deals-page-title">
              Deals for Students
            </h1>
            <p className="text-lg text-white/70">
              Discover shopping guides, everyday finds, and education insights for student life and beyond.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section-padding bg-white">
        <div className="container-custom">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-gray-100">
            <p className="text-gray-500 text-sm" data-testid="deals-article-count">
              Showing <span className="font-semibold text-primary">{filteredBlogs.length}</span> articles
            </p>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-56 rounded-sm border-gray-200" data-testid="category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent data-testid="category-filter-options">
                <SelectItem value="all" data-testid="category-option-all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.category_id} value={cat.name} data-testid={`category-option-${cat.slug}`}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blogs-grid">
              {filteredBlogs.map((post) => (
                <BlogCard key={post.post_id} post={post} />
              ))}
            </div>
          )}

          {!loading && filteredBlogs.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
